/**
 * Unit classes — the mechanism that makes "lead counts formatted as currency"
 * a REFUSED config rather than a rendered one.
 *
 * Every metric declares what it measures. Every display format declares which
 * unit classes it is allowed to render. The `unitMatch` constraint in
 * ./types.ts joins the two. Nothing else in the codebase is permitted to hold
 * an opinion about which formats suit which metrics.
 */

export type UnitClass = 'count' | 'currency' | 'percent';

export interface MetricDef {
  key: string;
  label: string;
  unit: UnitClass;
  /** Which data feeds actually carry this metric. */
  kinds: DataKind[];
}

export type DataKind = 'perf' | 'leads' | 'pacing' | 'static';

/**
 * The metric universe. `unit` is load-bearing: it is the sole input to format
 * validation. Adding a metric without a unit is a type error, by design.
 */
export const METRICS: MetricDef[] = [
  { key: 'leads', label: 'Leads', unit: 'count', kinds: ['perf', 'leads'] },
  { key: 'cost', label: 'Cost', unit: 'currency', kinds: ['perf'] },
  { key: 'clicks', label: 'Clicks', unit: 'count', kinds: ['perf'] },
  { key: 'impressions', label: 'Impressions', unit: 'count', kinds: ['perf'] },
  { key: 'cpl', label: 'CPL (cost per lead)', unit: 'currency', kinds: ['perf'] },
  { key: 'cpc', label: 'CPC (cost per click)', unit: 'currency', kinds: ['perf'] },
  { key: 'ctr', label: 'CTR', unit: 'percent', kinds: ['perf'] },
  { key: 'budget', label: 'Budget', unit: 'currency', kinds: ['pacing'] },
  { key: 'leads_target', label: 'Leads target', unit: 'count', kinds: ['pacing'] },
];

export const METRIC_BY_KEY: Record<string, MetricDef> = Object.fromEntries(
  METRICS.map((m) => [m.key, m]),
);

export interface FormatDef {
  key: string;
  label: string;
  /** Unit classes this format may render. Empty = renders anything (text). */
  accepts: UnitClass[];
}

/**
 * Display formats. `accepts` is the other half of the unit check.
 *
 * 'N' (compact) accepts both count and currency because "1.2k" is legible for
 * either; it deliberately does NOT accept percent, where compaction misleads.
 */
export const FORMATS: FormatDef[] = [
  { key: 'I', label: 'Integer (1,234)', accepts: ['count'] },
  { key: '$', label: 'Currency ($1,234.00)', accepts: ['currency'] },
  { key: 'P', label: 'Percent (12.34%)', accepts: ['percent'] },
  { key: 'N', label: 'Compact (1.2k)', accepts: ['count', 'currency'] },
];

export const FORMAT_BY_KEY: Record<string, FormatDef> = Object.fromEntries(
  FORMATS.map((f) => [f.key, f]),
);

/** Does `format` legally render `metric`? Used by the validator and the form. */
export function formatAcceptsMetric(formatKey: string, metricKey: string): boolean {
  const f = FORMAT_BY_KEY[formatKey];
  const m = METRIC_BY_KEY[metricKey];
  if (!f || !m) return false;
  return f.accepts.includes(m.unit);
}

/** The formats legal for a metric — drives the form's format dropdown. */
export function formatsForMetric(metricKey: string): FormatDef[] {
  const m = METRIC_BY_KEY[metricKey];
  if (!m) return FORMATS;
  return FORMATS.filter((f) => f.accepts.includes(m.unit));
}

/**
 * Dimensions, declared PER DATA FEED and grounded in what each feed physically
 * carries — not in what the old enum listed.
 *
 * This is the second silent-defect killer. The perf feed's rows have no
 * campaign column at all, so `dimension: 'campaign'` on a perf panel can never
 * resolve; today it silently renders models. It is absent here, so the
 * validator refuses it at publish.
 */
export interface DimensionDef {
  key: string;
  label: string;
  kinds: DataKind[];
}

export const DIMENSIONS: DimensionDef[] = [
  { key: 'model', label: 'Model', kinds: ['perf', 'leads'] },
  { key: 'channel', label: 'Channel', kinds: ['perf', 'leads'] },
  { key: 'promo', label: 'Promo', kinds: ['perf', 'leads'] },
  { key: 'funnel', label: 'Funnel stage', kinds: ['perf'] },
  { key: 'campaign', label: 'Campaign', kinds: ['leads'] },
  { key: 'date', label: 'Date', kinds: ['perf', 'leads'] },
  { key: 'week', label: 'Week', kinds: ['perf'] },
  { key: 'dayOfWeek', label: 'Day of week', kinds: ['perf'] },
  { key: 'branch', label: 'Branch', kinds: ['leads'] },
  { key: 'result', label: 'Result', kinds: ['leads'] },
  { key: 'type', label: 'Lead type', kinds: ['leads'] },
  { key: 'utmContent', label: 'UTM content', kinds: ['leads'] },
  { key: 'postcode', label: 'Postcode', kinds: ['leads'] },
];

export const dimensionsForKind = (kind: string): DimensionDef[] =>
  DIMENSIONS.filter((d) => d.kinds.includes(kind as DataKind));

export const metricsForKind = (kind: string): MetricDef[] =>
  METRICS.filter((m) => m.kinds.includes(kind as DataKind));
